// Import directly from the core implementation to avoid circular deps
import { defineStore } from 'pinia'
import { defaultParams } from '@/utils/constants'
import { getDeserializedCategoryProducts } from '@/utils/deserialization'

export const useSearchStore = defineStore('search', {
  state: () => ({
    results: 0,
    page: 0,
    products: [],
    autocompleteResults: [],
  }),

  getters: {
    hasResults: (state) => state.results > 0,
  },

  actions: {
    resetSearchResults() {
      this.results = 0
      this.page = 0
      this.products = []
    },

    addMoreSearchResults(results) {
      this.results = results.results
      this.products = [...this.products, ...results.products]
    },

    setSearchResultsPage(page) {
      this.page = page
    },

    setAutocompleteResults(results) {
      this.autocompleteResults = results
    },

    async getSearchResults(query, options = {}) {
      this.resetSearchResults()
      await this.loadMoreResults(query, options)
    },

    async loadMoreResults(query, options = {}) {
      const { rootStore = null, $axios = null } = options

      query = query || ''
      query = query.replace(/ +/g, '+')

      // Handle global loading/error states
      if (rootStore) {
        rootStore.removeError('search')
        rootStore.addLoading('search')
      }

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available')
        if (rootStore) {
          rootStore.addError({ id: 'search', error: new Error('No axios instance') })
          rootStore.removeLoading('search')
        }
        return
      }

      const nextPage = this.page + 1

      // Get coupon code from root store if available
      const couponCode = rootStore?.couponCode
      const params = {
        page: nextPage,
        ...(couponCode && { couponCode }),
        ...defaultParams,
      }

      try {
        const response = await axiosInstance.get(`search/${query}`, { params })
        const { data: searchResults } = response.data
        const { products } = searchResults
        const deserializedProducts = getDeserializedCategoryProducts(products)
        const deserializedSearchResults = {
          ...searchResults,
          products: deserializedProducts,
        }

        this.setSearchResultsPage(nextPage)
        this.addMoreSearchResults(deserializedSearchResults)
      } catch (error) {
        console.error('❌ Search error:', error)

        if (error.response?.status === 401 && rootStore) {
          // Handle authentication error with silent logout
          await rootStore.silentLogOut({ source: 'search/loadMoreResults' })

          try {
            const response = await axiosInstance.get(`search/${query}`, { params })
            const { data: searchResults } = response.data
            const { products } = searchResults
            const deserializedProducts = getDeserializedCategoryProducts(products)
            const deserializedSearchResults = {
              ...searchResults,
              products: deserializedProducts,
            }

            this.setSearchResultsPage(nextPage)
            this.addMoreSearchResults(deserializedSearchResults)
          } catch (retryError) {
            if (rootStore) {
              rootStore.addError({ id: 'search', error: retryError })
            }
          }
        }

        if (rootStore) {
          rootStore.addError({ id: 'search', error })
        }
      } finally {
        if (rootStore) {
          rootStore.removeLoading('search')
        }
      }
    },

    async getAutocompleteResults(query, options = {}) {
      const { rootStore = null, $axios = null } = options

      if (rootStore) {
        rootStore.removeError('autocomplete')
        rootStore.addLoading('autocomplete')
      }

      // Get axios from nuxt context if not provided
      const axiosInstance = $axios || (process.client && window.$nuxt ? window.$nuxt.$axios : null)

      if (!axiosInstance) {
        console.error('❌ No axios instance available for autocomplete')
        if (rootStore) {
          rootStore.addError({ id: 'autocomplete', error: new Error('No axios instance') })
          rootStore.removeLoading('autocomplete')
        }
        return
      }

      try {
        const params = { search: query, ...defaultParams }
        const response = await axiosInstance.get('/search/autocomplete', { params })

        const { data: results } = response.data

        this.setAutocompleteResults(results)
      } catch (error) {
        console.error('❌ Autocomplete error:', error)
        if (rootStore) {
          rootStore.addError({ id: 'autocomplete', error })
        }
      } finally {
        if (rootStore) {
          rootStore.removeLoading('autocomplete')
        }
      }
    },
  },
})
